<script>
  import { onDestroy, onMount } from "svelte"
  import LoadingSpinner from "../widgets/LoadingSpinner.svelte"
  import PdfPage from "./PdfPage.svelte"
  import { loadPdfDocument } from "./pdfjs"

  export let url

  const CONTAINER_PADDING = 32
  const FALLBACK_ERROR = "The PDF could not be loaded. Close the preview and try again."
  // Fit the widest page to the container without starting blown up.
  const FIT_MAX_SCALE = 1.5
  const MAX_ZOOM = 3
  const MIN_SCALE = 0.5
  const MIN_ZOOM = 0.5
  const ZOOM_STEP = 0.25

  let container
  let error = null
  let fitted = 1
  let pages = []
  let pdf = null
  let resizeObserver = null
  let widest = 0
  let zoom = 1

  // User zoom is a multiplier on the fitted scale, so a refit (resize,
  // rotation) preserves the chosen zoom level instead of resetting it.
  $: scale = fitted * zoom

  onMount(async () => {
    try {
      // Fetch the bytes here rather than handing pdf.js the URL so a failed
      // render can surface the server's plain-text error message.
      const response = await fetch(url)
      if (!response.ok) {
        const message = response.headers.get("content-type")?.includes("text/plain")
          ? await response.text()
          : null
        error = message || FALLBACK_ERROR
        return
      }
      const loaded = await loadPdfDocument({ data: new Uint8Array(await response.arrayBuffer()) })
      pdf = loaded.pdf
      widest = Math.max(...loaded.pages.map((page) => page.width))
      fit()
      pages = loaded.pages
      resizeObserver = new ResizeObserver(fit)
      resizeObserver.observe(container)
    } catch (e) {
      error = FALLBACK_ERROR
      throw e
    }
  })

  onDestroy(() => {
    resizeObserver?.disconnect()
    pdf?.loadingTask.destroy()
  })

  function fit() {
    // Skip while unrendered (e.g. inside a closed dialog): a zero width would
    // clamp the scale to the minimum for nothing.
    if (!widest || !container?.clientWidth) return
    const available = container.clientWidth - CONTAINER_PADDING
    const next = Math.min(FIT_MAX_SCALE, Math.max(MIN_SCALE, available / widest))
    // Ignore sub-percent jitter (e.g. a scrollbar toggling) so refits can't
    // oscillate or churn page re-renders.
    if (Math.abs(next - fitted) > 0.01) fitted = next
  }

  function zoomBy(step) {
    zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom + step))
  }
</script>

<div class="h-full relative">
  <div bind:this={container} class="bg-slate-100 flex flex-col gap-6 h-full overflow-auto p-4">
    {#if error}
      <p class="py-8 text-center text-red-500 text-sm">{error}</p>
    {:else if pages.length === 0}
      <div class="flex gap-2 items-center justify-center py-8 text-slate-600 text-sm">
        <LoadingSpinner classNames="h-4 w-4" />
        Loading PDF…
      </div>
    {:else}
      {#each pages as page (page.number)}
        <div
          class="bg-white mx-auto relative shadow shrink-0"
          style="height: {page.height * scale}px; width: {page.width * scale}px;"
        >
          <PdfPage {page} {pdf} {scale} />
        </div>
      {/each}
    {/if}
  </div>

  {#if pages.length > 0}
    <span
      class="
        absolute bg-white border bottom-4 divide-x flex h-7 items-center overflow-hidden
        right-8 rounded shadow-md
      "
    >
      <button
        class="h-full hover:bg-slate-100 leading-none w-8"
        title="Zoom out"
        type="button"
        on:click={() => zoomBy(-ZOOM_STEP)}
      >
        −
      </button>
      <button
        class="
          bg-slate-50 flex h-full hover:bg-slate-100 items-center justify-center tabular-nums
          text-slate-600 text-xs w-12
        "
        title="Reset zoom"
        type="button"
        on:click={() => (zoom = 1)}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button
        class="h-full hover:bg-slate-100 leading-none w-8"
        title="Zoom in"
        type="button"
        on:click={() => zoomBy(ZOOM_STEP)}
      >
        +
      </button>
    </span>
  {/if}
</div>
